/**
 * LegacyHashRedirect — 90-day migration shim for old query-string
 * tenant URLs.
 *
 * The legacy URL shape was `/#/path?tenant=team-alpha`. After the
 * switch to BrowserRouter + path-based `/:tenantSlug/...`, those
 * URLs land on the empty root with a stale hash. This component
 * runs ONCE on mount (before any other render), detects the legacy
 * shape, rewrites the URL to the new path, and `window.location.replace`s
 * so the browser doesn't keep the old form in history.
 *
 * Mounted at the very top of `main.tsx` BEFORE React renders so the
 * redirect happens before any route resolution.
 *
 * Remove after 2026-08-25 (90 days from the path-based refactor
 * landing in production). Tracked by ticket — search the codebase
 * for `LegacyHashRedirect` to find every reference.
 */

import { isReservedSlug } from './reservedSlugs';

/**
 * Inspects window.location for a legacy hash URL and rewrites it.
 * Returns true if a redirect was issued (caller should not continue
 * rendering — the browser is about to navigate).
 */
export function maybeRedirectLegacyHash(): boolean {
  if (typeof window === 'undefined') return false;

  const hash = window.location.hash || '';
  if (!hash.startsWith('#/')) return false;

  // Strip the leading `#` → get the legacy "pathname?search" portion.
  const legacy = hash.slice(1); // e.g. `/login?tenant=team-alpha`
  const [legacyPath, legacyQuery = ''] = legacy.split('?');
  const params = new URLSearchParams(legacyQuery);
  const tenant = (params.get('tenant') || '').trim().toLowerCase();
  params.delete('tenant');

  // Strip legacy brand-preview slugs (`/_/m2`, `/_t/<tenant>`) too —
  // the preset preview escape hatch is gone; brand resolves from the
  // tenant slug only. `/_/X` maps to `/login` (no tenant); `/_t/X`
  // maps to `/X/login` (a branded login for tenant X).
  const presetMatch = legacyPath.match(/^\/_\/([a-z0-9-]+)/i);
  const tenantPathMatch = legacyPath.match(/^\/_t\/([a-z0-9-]+)/i);

  let newPath: string;
  if (tenantPathMatch && tenantPathMatch[1]) {
    newPath = `/${tenantPathMatch[1].toLowerCase()}/login`;
  } else if (presetMatch) {
    // No tenant context — fall back to the platform default's
    // branded login (handled by the /login route's redirect).
    newPath = `/login`;
  } else if (tenant && !isReservedSlug(tenant)) {
    // Prefix the legacy path with the tenant slug. Reserved
    // legacy paths (/login, /super-admin, /terms, /privacy) stay
    // tenant-less — those routes are public/global in the new model.
    const firstSeg = (legacyPath.split('/')[1] || '').toLowerCase();
    if (isReservedSlug(firstSeg)) {
      // e.g. `#/login?tenant=team-alpha` → `/team-alpha/login`
      // (the tenant version of login is branded; the global /login
      // is for unbranded sessions)
      if (firstSeg === 'login') {
        newPath = `/${tenant}/login` + legacyPath.replace(/^\/login/, '');
      } else {
        newPath = legacyPath;
      }
    } else {
      newPath = `/${tenant}${legacyPath || '/'}`;
    }
  } else {
    newPath = legacyPath || '/';
  }

  // Re-attach any remaining (non-tenant) search params.
  const remainingQuery = params.toString();
  const target = newPath + (remainingQuery ? `?${remainingQuery}` : '');

  // Use replace so the legacy URL doesn't appear in back history.
  window.location.replace(target);
  return true;
}
